import { useEffect, useState } from "react";
import type { Rule } from "antd/es/form";
import { Alert, Button, Form, Input, Popconfirm, Select, Switch, Tooltip, theme } from "antd";
import { TbArchive, TbArrowBackUp } from "react-icons/tb";
import { Stage, LifeCycle } from "../../dtos/stage.dto";
import { LIFECYCLE_META, LIFECYCLE_ORDER, toStageValue, StageColor, StageIconTag } from "./constants";
import { StageColorPicker, StageIconPicker } from "./picker";

export type StageFormValues = Omit<Stage.Create, "resource">;

interface StageFormProps {
  mode: "create" | "edit";
  stage: Stage.Dto | null;
  resourceLabel: string;
  saving: boolean;
  takenValues: string[];
  conflictingEntryLabel?: string;
  onSubmit: (values: StageFormValues) => void;
  onArchive: () => void;
  onRestore: () => void;
  onCancel: () => void;
}

const DEFAULTS: StageFormValues = {
  label: "",
  value: "",
  lifecycle: LifeCycle.INTERMEDIATE,
  icon: StageIconTag.FILE,
  color: StageColor.INDIGO,
  public: false,
};

const StageForm = ({
  mode,
  stage,
  resourceLabel,
  saving,
  takenValues,
  conflictingEntryLabel,
  onSubmit,
  onArchive,
  onRestore,
  onCancel,
}: StageFormProps) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm<StageFormValues>();
  const [valueDirty, setValueDirty] = useState(false);

  const color = Form.useWatch("color", form);
  const lifecycle = Form.useWatch("lifecycle", form);

  const isEdit = mode === "edit" && stage !== null;
  const isLocked = Boolean(stage?.locked);
  const isArchived = Boolean(stage?.archived);

  const valueRules: Rule[] =
    mode === "create"
      ? [
          { required: true, message: "A key is required" },
          {
            pattern: /^[a-z0-9]+(_[a-z0-9]+)*$/,
            message: "Lowercase letters, numbers and underscores only",
          },
          {
            validator: (_, current: string) =>
              current && takenValues.includes(current)
                ? Promise.reject(new Error("Another stage already uses this key"))
                : Promise.resolve(),
          },
        ]
      : [];

  useEffect(() => {
    setValueDirty(mode === "edit");
    form.setFieldsValue(
      stage
        ? {
            label: stage.label,
            value: stage.value,
            lifecycle: stage.lifecycle,
            icon: stage.icon as StageIconTag,
            color: stage.color as StageColor,
            public: stage.public,
          }
        : DEFAULTS,
    );
  }, [form, mode, stage]);

  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (mode !== "create" || valueDirty) return;
    form.setFieldValue("value", toStageValue(event.target.value));
  };

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={DEFAULTS}
      disabled={isArchived}
      onFinish={onSubmit}
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        <h3
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: token.colorText,
          }}
        >
          {isEdit ? stage.label : `New ${resourceLabel.toLowerCase()} stage`}
        </h3>
        <p
          style={{
            margin: "2px 0 18px",
            fontSize: 13,
            color: token.colorTextTertiary,
          }}
        >
          {isEdit
            ? "Changes apply everywhere this stage is shown."
            : "Give the stage a name, pick where it sits in the lifecycle."}
        </p>

        {isArchived ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="This stage is archived"
            description="Archived stages stay on existing records but can't be picked for new ones. Restore it to edit."
          />
        ) : null}

        {isLocked && !isArchived ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="System stage"
            description="The name and look are yours to change. The key and lifecycle are fixed, and it can't be archived."
          />
        ) : null}

        <Form.Item
          name="label"
          label="Name"
          rules={[
            { required: true, message: "Give the stage a name" },
            { max: 48, message: "Keep it under 48 characters" },
          ]}
        >
          <Input placeholder="Docs under review" onChange={handleLabelChange} maxLength={48} />
        </Form.Item>

        <Form.Item
          name="value"
          label="Key"
          tooltip="Used by automations and the API. Set once, then fixed."
          rules={valueRules}
        >
          <Input
            placeholder="docs_under_review"
            disabled={isEdit}
            onChange={() => setValueDirty(true)}
            style={{ fontFamily: token.fontFamilyCode }}
          />
        </Form.Item>

        <Form.Item
          name="lifecycle"
          label="Lifecycle"
          rules={[{ required: true, message: "Pick a lifecycle" }]}
          // extra={
          //   lifecycle === LifeCycle.ENTRY && conflictingEntryLabel
          //     ? `${conflictingEntryLabel} is already the entry stage for ${resourceLabel.toLowerCase()}. Two entry stages make the landing stage ambiguous.`
          //     : LIFECYCLE_META[lifecycle as LifeCycle]?.description
          // }
        >
          <Select
            // disabled={isLocked}
            options={LIFECYCLE_ORDER.map((item) => ({
              value: item,
              label: LIFECYCLE_META[item].label,
            }))}
            optionRender={(option) => {
              const meta = LIFECYCLE_META[option.value as LifeCycle];
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <meta.Icon size={16} color={meta.color.text} />
                  <span>
                    <span style={{ display: "block", fontSize: 13 }}>{meta.label}</span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 11,
                        lineHeight: 1.4,
                        color: token.colorTextTertiary,
                      }}
                    >
                      {meta.description}
                    </span>
                  </span>
                </div>
              );
            }}
          />
        </Form.Item>

        <Form.Item name="icon" label="Icon" rules={[{ required: true, message: "Pick an icon" }]}>
          <StageIconPicker color={color} />
        </Form.Item>

        <Form.Item name="color" label="Color" rules={[{ required: true, message: "Pick a colour" }]}>
          <StageColorPicker />
        </Form.Item>

        <Form.Item
          name="public"
          label="Share with other organisations"
          valuePropName="checked"
          hidden={isLocked}
          extra="They can see this stage in your pipeline but can't use it in theirs."
        >
          <Switch />
        </Form.Item>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 24px",
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          background: token.colorBgContainer,
        }}
      >
        {isEdit && !isArchived ? (
          <Tooltip title={isLocked ? "System stages can't be archived" : undefined}>
            <Popconfirm
              title="Archive this stage?"
              description="Records already on it keep it. Nobody can pick it going forward."
              okText="Archive"
              okButtonProps={{ danger: true }}
              disabled={isLocked}
              onConfirm={onArchive}
            >
              <Button danger type="text" disabled={isLocked} icon={<TbArchive size={16} />}>
                Archive
              </Button>
            </Popconfirm>
          </Tooltip>
        ) : null}

        {isArchived ? (
          <Button
            type="default"
            disabled={false}
            loading={saving}
            icon={<TbArrowBackUp size={16} />}
            onClick={onRestore}
          >
            Restore stage
          </Button>
        ) : null}

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Button onClick={onCancel} disabled={false}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={saving} disabled={isArchived}>
            {isEdit ? "Save changes" : "Create stage"}
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default StageForm;
